import { useRef, useState } from 'react';
import { FC } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Hidden,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import HtmlTooltip from 'src/components/HtmlTooltip';
import useAuth from 'src/hooks/useAuth';
import { riwLog } from 'src/hooks/riw-logs';
import DownloadAppModal from 'src/components/DownloadAppModal';

const Account: FC = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isDownloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const history = useHistory();

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      history.replace('/login');
    } catch (err) {
      riwLog(
        'error',
        'Account',
        `User Name ${user.name}`,
        'Unable to Logout',
        err
      );
      enqueueSnackbar('Unable to logout', {
        variant: 'error',
      });
    }
  };

  return (
    <Box>
      <HtmlTooltip title="Settings">
        <Box
          component={ButtonBase}
          onClick={handleOpen}
          ref={ref}
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Avatar
            alt="User"
            src={user.avatar}
            sx={{
              height: 32,
              width: 32,
              marginRight: 1,
            }}
          />
          <Hidden mdDown>
            <Typography variant="h6" color="inherit">
              {user.name}
            </Typography>
          </Hidden>
        </Box>
      </HtmlTooltip>
      <Menu
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        keepMounted
        PaperProps={{
          sx: {
            width: 200,
          },
        }}
        anchorEl={ref.current}
        open={isOpen}
      >
        <MenuItem
          component={RouterLink}
          to="/app/profile"
          onClick={handleClose}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setDownloadModalOpen(true);
          }}
        >
          Download App
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
      <DownloadAppModal
        open={isDownloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
      />
    </Box>
  );
};

export default Account;
